// utils/phoneFormatter.ts

interface CountryFormat {
    code: string;
    flag: string;
    pattern: RegExp;
    format: (number: string) => string;
    priority: number; // Higher = checked first
}

const countryFormats: CountryFormat[] = [
    // Trinidad & Tobago (868)
    {
        code: 'TT',
        flag: '🇹🇹',
        pattern: /^(?:1?)(868)(\d{7})$/,
        format: (num) => {
            const cleaned = num.replace(/^1/, '');
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
        },
        priority: 10
    },
    // US & Canada
    {
        code: 'US',
        flag: '🇺🇸',
        pattern: /^(?:1?)([2-9]\d{2})(\d{3})(\d{4})$/,
        format: (num) => {
            const cleaned = num.replace(/^1/, '');
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
        },
        priority: 9
    },
    // Jamaica (876)
    {
        code: 'JM',
        flag: '🇯🇲',
        pattern: /^(?:1?)(876)(\d{7})$/,
        format: (num) => {
            const cleaned = num.replace(/^1/, '');
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
        },
        priority: 10
    },
    // Barbados (246)
    {
        code: 'BB',
        flag: '🇧🇧',
        pattern: /^(?:1?)(246)(\d{7})$/,
        format: (num) => {
            const cleaned = num.replace(/^1/, '');
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
        },
        priority: 10
    },
    // Guyana (592)
    {
        code: 'GY',
        flag: '🇬🇾',
        pattern: /^(592)(\d{7})$/,
        format: (num) => `(${num.slice(0, 3)}) ${num.slice(3, 6)}-${num.slice(6, 10)}`,
        priority: 10
    },
    // UK (44)
    {
        code: 'GB',
        flag: '🇬🇧',
        pattern: /^(44)(\d{3})(\d{3})(\d{4})$/,
        format: (num) => `+${num.slice(0, 2)} ${num.slice(2, 5)} ${num.slice(5, 8)} ${num.slice(8, 12)}`,
        priority: 10
    },
    // Venezuela (58)
    {
        code: 'VE',
        flag: '🇻🇪',
        pattern: /^(58)(\d{3})(\d{7})$/,
        format: (num) => `+${num.slice(0, 2)} ${num.slice(2, 5)}-${num.slice(5, 12)}`,
        priority: 10
    },
    // Other Caribbean (generic 7-digit with 3-digit area code)
    {
        code: 'CAR',
        flag: '🌴',
        pattern: /^(?:1?)([2-9]\d{2})(\d{7})$/,
        format: (num) => {
            const cleaned = num.replace(/^1/, '');
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
        },
        priority: 1
    }
];

export const formatPhoneNumber = (
    phone: string | number,
    options?: { showFlag?: boolean; countryCode?: string }
): string => {
    const numStr = String(phone).replace(/\D/g, '');
    const { showFlag = true, countryCode } = options || {};
    
    // Find matching country format
    let matchedFormat: CountryFormat | undefined;
    
    if (countryCode) {
        matchedFormat = countryFormats.find(f => f.code === countryCode);
    } else {
        // Sort by priority (higher first) and find first match
        matchedFormat = [...countryFormats]
            .sort((a, b) => b.priority - a.priority)
            .find(format => format.pattern.test(numStr));
    }
    
    const format = matchedFormat || countryFormats[countryFormats.length - 1];
    const formatted = format.format(numStr);
    
    return showFlag ? `${format.flag} ${formatted}` : formatted;
};

// Usage examples:
// formatPhoneNumber('18686891234') → "🇹🇹 (868) 689-1234"
// formatPhoneNumber('18686891234', { showFlag: false }) → "(868) 689-1234"
// formatPhoneNumber('8765551234') → "🇯🇲 (876) 555-1234"
// formatPhoneNumber('442071234567') → "🇬🇧 +44 207 123 4567"
// formatPhoneNumber('584121234567') → "🇻🇪 +58 412-1234567"
// formatPhoneNumber('2465551234') → "🇧🇧 (246) 555-1234"
// formatPhoneNumber('5925551234') → "🇬🇾 (592) 555-1234"